// @ts-check
import {
    CognitoIdentityProviderClient,
    InitiateAuthCommand,
    RespondToAuthChallengeCommand,
    GlobalSignOutCommand,
    ConfirmSignUpCommand,
    ResendConfirmationCodeCommand,
    ConfirmForgotPasswordCommand,
    VerifyUserAttributeCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/browser';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';

/**
 * @typedef {import('@aws-sdk/client-cognito-identity-provider').AuthenticationResultType} AuthenticationResultType
 */

/**
 * Handle auth-related tasks like login, getting/refreshing the Flash ID token, and maintaining logged-in state.
 *
 * See also middleware/auth.js.
 *
 * The API and behavior of $auth is based on Nuxt Auth (@nuxtjs/auth-next), and our own custom FlashScheme,
 * which was used to build flash-ui. However, Nuxt Auth is outdated, poorly maintained, and confusing.
 * We were also duplicating some of its behavior.
 *
 * This plugin was written to replace Nuxt Auth, while minimizing changes to the pages that used it.
 * There are probably many ways it could be made better.
 *
 * This plugin uses the AWS SDK v3 CognitoIdentityProvider client to handle authentication with Cognito.
 * It replaces the previous implementation that used aws-amplify.
 */
export default ({
    $config, $api, store,
}, inject) => {
    const cognitoClient = new CognitoIdentityProviderClient({
        region: $config.AWS_COGNITO_REGION,
    });

    // The cookie name is env-specific so a broader-scoped cookie from another
    // environment (e.g. int's `flash.id_token` on `energysage.dev`, which also
    // reaches `*.dev.energysage.dev`) can't collide with this one. Defaults to
    // the shared name for int/prod. See COOKIE_NAME in the .env files.
    const GLOBAL_COOKIE_NAME = $config.COOKIE_NAME || 'flash.id_token';
    const GLOBAL_COOKIE_SETTINGS = {
        domain: $config.COOKIE_DOMAIN,
        secure: $config.COOKIE_SECURE,
    };

    const STORAGE_KEY = `CognitoIdentityServiceProvider.${$config.AWS_COGNITO_CLIENT_ID}`;
    const TOKEN_STORAGE_KEY = `${STORAGE_KEY}.tokens`;
    const USERNAME_STORAGE_KEY = `${STORAGE_KEY}.Username`;

    /**
     * Use localStorage for OAuth tokens and username.
     */
    const storage = {
        /**
         * Get tokens from localStorage.
         * @returns {AuthenticationResultType|null} The stored tokens or null if not found
         */
        get tokens() {
            const tokensJson = localStorage.getItem(TOKEN_STORAGE_KEY);
            if (tokensJson) {
                try {
                    return JSON.parse(tokensJson);
                } catch (err) {
                    // If we can't parse the tokens, just remove them and start fresh
                    localStorage.removeItem(TOKEN_STORAGE_KEY);
                    return null;
                }
            }

            // Try to migrate from the old aws-amplify format

            const lastAuthUser = localStorage.getItem(`${STORAGE_KEY}.LastAuthUser`);
            if (!lastAuthUser) return null;

            const idToken = localStorage.getItem(`${STORAGE_KEY}.${lastAuthUser}.idToken`);
            const accessToken = localStorage.getItem(`${STORAGE_KEY}.${lastAuthUser}.accessToken`);
            const refreshToken = localStorage.getItem(`${STORAGE_KEY}.${lastAuthUser}.refreshToken`);

            if (idToken && accessToken && refreshToken) {
                this.username = lastAuthUser;

                const tokens = {
                    IdToken: idToken,
                    AccessToken: accessToken,
                    RefreshToken: refreshToken,
                };
                this.tokens = tokens;

                // Clean up old aws-amplify data
                localStorage.removeItem(`${STORAGE_KEY}.LastAuthUser`);
                localStorage.removeItem(`${STORAGE_KEY}.${lastAuthUser}.idToken`);
                localStorage.removeItem(`${STORAGE_KEY}.${lastAuthUser}.accessToken`);
                localStorage.removeItem(`${STORAGE_KEY}.${lastAuthUser}.refreshToken`);
                localStorage.removeItem(`${STORAGE_KEY}.${lastAuthUser}.clockDrift`);
                localStorage.removeItem(`${STORAGE_KEY}.${lastAuthUser}.userData`);

                return tokens;
            }

            return null;
        },

        /**
         * Store tokens in localStorage and set the global cookie.
         * @param {AuthenticationResultType|null} tokens The tokens to store, or null to clear
         */
        set tokens(tokens) {
            if (!tokens) {
                localStorage.removeItem(TOKEN_STORAGE_KEY);
                Cookies.remove(GLOBAL_COOKIE_NAME, GLOBAL_COOKIE_SETTINGS);
                return;
            }

            localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));

            if (tokens.IdToken) {
                Cookies.set(
                    GLOBAL_COOKIE_NAME,
                    tokens.IdToken,
                    {
                        ...GLOBAL_COOKIE_SETTINGS,
                        // Our backends use the ID token to authorize API requests, so it needs to live longer than
                        // the browser session. Setting 1 day cookie expiration to match Cognito token expiration.
                        // However, the token may expire sooner, depending on when this function is called.
                        // There might also be a case for the cookie expiration matching the refreshToken expiration
                        // (4 weeks), e.g. to allow frontends to determine if the user has previously logged in.
                        expires: 1,
                    },
                );
            }
        },

        /**
         * Get username from localStorage.
         * @returns {string|null} The stored username or null if not found
         */
        get username() {
            return localStorage.getItem(USERNAME_STORAGE_KEY);
        },

        /**
         * Store username in localStorage.
         * @param {string|null} username The username to store, or null to clear
         */
        set username(username) {
            if (username) {
                localStorage.setItem(USERNAME_STORAGE_KEY, username);
            } else {
                localStorage.removeItem(USERNAME_STORAGE_KEY);
            }
        },

        /**
         * Refresh tokens if needed or requested.
         * @param {object} args
         * @param {boolean} [args.forceRefresh] - Force token refresh regardless of expiration
         * @returns {Promise<AuthenticationResultType>} The current or refreshed tokens
         */
        async refreshTokens({ forceRefresh } = {}) {
            // Reads from localStorage via the getter function
            let { tokens } = this;

            if (!tokens) {
                throw new Error('No tokens found');
            }

            let tokenExpired;
            try {
                /** @type {object} */
                const decodedToken = jwtDecode(tokens.IdToken);
                const currentTime = Math.floor(Date.now() / 1000);
                tokenExpired = decodedToken.exp <= currentTime + 300; // 5 minute grace period
            } catch (error) {
                tokenExpired = true;
            }

            if ((tokenExpired || forceRefresh) && tokens.RefreshToken) {
                try {
                    const command = new InitiateAuthCommand({
                        AuthFlow: 'REFRESH_TOKEN_AUTH',
                        ClientId: $config.AWS_COGNITO_CLIENT_ID,
                        AuthParameters: {
                            REFRESH_TOKEN: tokens.RefreshToken,
                        },
                    });

                    const response = await cognitoClient.send(command);

                    if (!response.AuthenticationResult) {
                        throw new Error('Token refresh failed');
                    }

                    // Keep the current refresh token since it's not included in the refresh response
                    tokens = {
                        ...response.AuthenticationResult,
                        RefreshToken: this.tokens.RefreshToken,
                    };
                } catch (error) {
                    // If refresh fails, clear tokens to force re-login
                    this.clear();
                    throw error;
                }
            }

            // The this.tokens *setter* syncs the tokens back the shared cookie
            // and localStorage.
            this.tokens = tokens;
            return tokens;
        },

        /**
         * Clear all stored auth data.
         */
        clear() {
            this.tokens = null;
            this.username = null;
        },
    };

    inject('auth', {
        /** @type {string|null} - Store the current session during authentication flows. */
        cognitoSession: null,

        /**
         * Confirm a user's registration with confirmation code.
         *
         * @param {object} args
         * @param {string} args.username User's full username in tenant format
         * @param {string} args.code The confirmation code received
         * @returns {Promise<void>} Confirmation result - always returns "SUCCESS" for backward compatibility
         */
        async confirmSignUp({ username, code }) {
            await cognitoClient.send(new ConfirmSignUpCommand({
                ClientId: $config.AWS_COGNITO_CLIENT_ID,
                Username: username,
                ConfirmationCode: code,
            }));
        },

        /**
         * Resend confirmation code for registration.
         *
         * @param {object} args
         * @param {string} args.username User's full username in tenant format
         * @returns {Promise<void>}
         */
        async resendConfirmationCode({ username }) {
            await cognitoClient.send(new ResendConfirmationCodeCommand({
                ClientId: $config.AWS_COGNITO_CLIENT_ID,
                Username: username,
            }));
        },

        /**
         * Reset password using verification code.
         *
         * @param {object} args
         * @param {string} args.username Username (usually from query params)
         * @param {string} args.code Verification code
         * @param {string} args.newPassword New password
         * @returns {Promise<void>}
         */
        async resetPassword({ username, code, newPassword }) {
            await cognitoClient.send(new ConfirmForgotPasswordCommand({
                ClientId: $config.AWS_COGNITO_CLIENT_ID,
                Username: username,
                ConfirmationCode: code,
                Password: newPassword,
            }));
        },

        /**
         * Verify user attribute (like email) with a verification code.
         *
         * @param {object} args
         * @param {string} args.attribute The attribute to verify (e.g. 'email')
         * @param {string} args.code Verification code
         * @returns {Promise<void>}
         */
        async verifyUserAttribute({ attribute, code }) {
            await storage.refreshTokens();

            await cognitoClient.send(new VerifyUserAttributeCommand({
                AccessToken: storage.tokens.AccessToken,
                AttributeName: attribute,
                Code: code,
            }));
        },

        /**
         * Begin a custom authentication flow (like Auto OTP or OTP Code).
         *
         * @param {object} args
         * @param {string} args.username User's username or ID
         * @param {string} args.signInMethod Authentication method to use (AUTO_OTP, OTP_CODE, or MAGIC_LINK)
         * @param {object} [args.additionalClientMetadata] Additional client metadata to send for the custom auth flow
         * @returns {Promise<void>}
         */
        async beginAuthFlow({ username, signInMethod, additionalClientMetadata = {} }) {
            this.logUserId(username);

            store.commit('setAuthProgress', 5);

            // Start the custom auth challenge flow, implemented in flash-auth Lambdas.
            performance.mark('start-auth');

            const initiateResponse = await cognitoClient.send(new InitiateAuthCommand({
                AuthFlow: 'CUSTOM_AUTH',
                ClientId: $config.AWS_COGNITO_CLIENT_ID,
                AuthParameters: {
                    USERNAME: username,
                },
            }));

            this.cognitoSession = initiateResponse.Session;

            storage.username = username;

            store.commit('setAuthProgress', 25);

            // The first challenge from the flash-auth Lambdas is PROVIDE_AUTH_PARAMETERS, to determine the
            // actual challenge to use for authentication. Indicate that we want the AUTO_OTP, OTP_CODE, or MAGIC_LINK
            // challenge.
            const respondResponse = await cognitoClient.send(new RespondToAuthChallengeCommand({
                ChallengeName: 'CUSTOM_CHALLENGE',
                ClientId: $config.AWS_COGNITO_CLIENT_ID,
                ChallengeResponses: {
                    USERNAME: storage.username || '',
                    ANSWER: 'n/a',
                },
                Session: this.cognitoSession,
                ClientMetadata: { signInMethod, ...additionalClientMetadata },
            }));

            this.cognitoSession = respondResponse.Session;
            if (respondResponse.ChallengeParameters.error === 'RATE_LIMITED') {
                throw new Error('RATE_LIMITED');
            }
            store.commit('setAuthProgress', 50);
        },

        /**
         * Complete a custom authentication flow and log the user in.
         *
         * @param {object} args
         * @param {string} args.challengeAnswer The answer to the challenge (code or token)
         * @param {string} args.signInMethod Authentication method in use (AUTO_OTP, OTP_CODE, or MAGIC_LINK)
         * @returns {Promise<void>}
         */
        async completeAuthFlow({ challengeAnswer, signInMethod }) {
            if (!this.cognitoSession) {
                throw new Error('No active session');
            }

            // The next challenge from the flash-auth Lambdas is either AUTO_OTP, OTP_CODE, or MAGIC_LINK.
            // Respond with the Auto OTP token or the OTP Code to log in and get OAuth tokens.
            const response = await cognitoClient.send(new RespondToAuthChallengeCommand({
                ChallengeName: 'CUSTOM_CHALLENGE',
                ClientId: $config.AWS_COGNITO_CLIENT_ID,
                ChallengeResponses: {
                    USERNAME: storage.username || '',
                    ANSWER: challengeAnswer,
                },
                Session: this.cognitoSession,
                ClientMetadata: { signInMethod },
            }));

            this.cognitoSession = response.Session;

            if (!response.AuthenticationResult) {
                // This is most likely an invalid OTP code. On the 5th failed attempt,
                // RespondToAuthChallengeCommand will throw NotAuthorizedException.
                throw new Error(`${signInMethod} response failed`);
            }

            storage.tokens = response.AuthenticationResult;

            performance.mark('end-auth');

            store.commit('setAuthProgress', 75);

            // Record auth duration metrics
            const authMeasure = performance.measure('auth', 'start-auth', 'end-auth');
            datadogRum.addAction('auth', { duration: authMeasure.duration, method: signInMethod });

            /** @type {object} */
            const decodedToken = jwtDecode(storage.tokens.IdToken);
            this.logEmail(decodedToken.email);

            await this.fetchUser();

            store.commit('setAuthProgress', 100);
        },

        /**
         * Log the user in to Cognito with a password, and get their Flash Profile.
         *
         * This will create a Cognito session in localStorage, which includes the ID and refresh tokens.
         * However, those values should only be accessed through Auth functions.
         *
         * For OTP login, see /pages/login/otp.vue::handleCodeSubmit()
         *
         * @param {object} args
         * @param {string} args.username Must be in the form `${tenantId}::${email}`, likely via getTenantUsername
         * @param {string} args.password The user's password
         * @returns {Promise<void>}
         */
        async login({ username, password }) {
            if (!username) {
                throw new Error('Username missing');
            }

            if (!password) {
                throw new Error('Password missing');
            }

            const response = await cognitoClient.send(new InitiateAuthCommand({
                AuthFlow: 'USER_PASSWORD_AUTH',
                ClientId: $config.AWS_COGNITO_CLIENT_ID,
                AuthParameters: {
                    USERNAME: username,
                    PASSWORD: password,
                },
            }));

            if (!response.AuthenticationResult) {
                throw new Error('Login failed');
            }

            storage.tokens = response.AuthenticationResult;
            storage.username = username;

            await this.fetchUser();
        },

        /**
         * Set $auth.user (and $auth.loggedIn) after the user is signed-in to Cognito.
         * @returns {Promise<void>}
         */
        async fetchUser() {
            // Only set the user once per session
            if (this.user) return;

            performance.mark('start-getUser');

            await storage.refreshTokens();

            const userAttributes = await $api.getUser({ idToken: storage.tokens.IdToken });
            if (!userAttributes) {
                throw new Error('No user info found');
            }

            performance.mark('end-getUser');

            const getUserMeasure = performance.measure('getUser', 'start-getUser', 'end-getUser');
            datadogRum.addAction('getUser', { duration: getUserMeasure.duration });

            const user = {
                flashUserId: userAttributes.userId,
                email: userAttributes.email,
                emailVerified: userAttributes.emailVerified,
                firstName: userAttributes.firstName,
                lastName: userAttributes.lastName,
                tenantId: userAttributes.tenantId,
            };

            store.commit('setUser', user);
        },

        /**
         * Get the Flash ID token, and set the global cookie.
         *
         * Assumes that a Cognito session has been established, e.g. by $auth.login().
         *
         * This takes advantage of token refresh handled by getCurrentTokens.
         *
         * @param {object} args
         * @param {boolean} [args.forceRefresh] Refresh the ID token, even if it's not expired
         * @returns {Promise<string>}
         */
        async getIdToken({ forceRefresh } = {}) {
            try {
                await storage.refreshTokens({ forceRefresh });
                return storage.tokens.IdToken;
            } catch (error) {
                // With no valid tokens, the user is no longer logged in
                this.reset();
                throw error;
            }
        },

        /**
         * Get the Flash Profile data for the logged-in user.
         * @returns {object} The user data set in fetchUser()
         */
        get user() {
            // Assuming user will be set/cleared by other methods.
            return store.state.user;
        },

        /**
         * Convenience attribute, carried over from Nuxt Auth API.
         * @returns {boolean} Whether the user is logged in
         */
        get loggedIn() {
            return Boolean(this.user);
        },

        /**
         * Log the user out of Cognito, and clear local state.
         * @returns {Promise<void>}
         */
        async logout() {
            try {
                await storage.refreshTokens();

                await cognitoClient.send(new GlobalSignOutCommand({
                    AccessToken: storage.tokens.AccessToken,
                }));
            } catch (error) {
                // signOut failure shouldn't prevent logout
                this.logError(error instanceof Error ? error : new Error(String(error)), { method: 'logout' });
            }

            this.reset();
        },

        /**
         * Clear local state.
         */
        reset() {
            this.cognitoSession = null;
            store.commit('setUser', null);
            storage.clear();
        },

        /**
         * Log the user's ID early, to help us find sessions where the user fails to log in.
         * TODO: Combine this and logEmail
         * @param {string} id The user's email
         */
        logUserId(id) {
            datadogRum.setUserProperty('id', id);
            Sentry.setUser({ id });
        },

        /**
         * Log the user's email early, to help us find sessions where the user fails to log in.
         * TODO: Combine this and logUserId
         * @param {string} email The user's email
         */
        logEmail(email) {
            datadogRum.setUserProperty('email', email);
            Sentry.setUser({ email });
        },

        /**
         * Log errors to Sentry and Datadog.
         *
         * This method is based on logging functionality from the old Nuxt Auth implementation,
         * to reduce impact on the calling code. It should probably be its own plugin, and we should revisit how
         * logging is handled; @see https://energysage.atlassian.net/browse/PLAT-1188.
         *
         * The `error` argument must be `instanceof Error`, but some AWS methods
         * throw strings. To ensure an `Error`, conditionally wrap the argument to `logError`:
         *
         *     err instanceof Error ? err : new Error(err)
         *
         * Doing this for each call is clunky, but it gives a more useful stack trace.
         *
         * @param {Error} error The original error object
         * @param {object} args
         * @param {string} args.method The name of the method where the error occurred
         */
        logError(error, { method }) {
            Sentry.captureException(error, { extra: { method } });
            datadogRum.addError(error, { method });
        },
    });
};
